/**
 * Bridge store — Context + useReducer + an imperative action layer.
 *
 * The reducer (reducer.ts) holds every *synchronous* mutation. This provider
 * wraps it with the impure bits the source class owned inline: timers
 * (connect/ingest/resync), toast scheduling, Date.now() ids, and the
 * record() timestamp. Components consume `useBridge()` and call methods that
 * read 1:1 against the original component's methods.
 */
import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { reducer, type Action } from './reducer';
import { initialState, type State } from './state';
// FE↔BE integration (Round 1): boot-time directory load from the real backend.
import { USE_BACKEND } from '../api/config';
import { loadDirectory, applyBackendIntegrations, seedBindings, refreshConnState } from '../api/directory';
// FE↔BE integration (Round 2): connection lifecycle.
import { createConnection, reauthorizeConnection, revokeConnection, startMcpOAuth, startRestOAuth } from '../api/connections';
// FE↔BE integration (Round 3): grants/routes.
import { createGrant, revokeGrant, SingleActiveRouteError } from '../api/grants';
import { mapBinding } from '../api/mappers';
// FE↔BE integration (Round 4): observability (logs modal).
import { loadObservability } from '../api/observability';
// FE↔BE integration (Rounds 8+9): builder/versioning (ingest, approve/publish).
import { ingestVendor, approveVersion, listVersions, autoMapEntity, approveEntityMapping } from '../api/builder';
// FE↔BE integration (Round 5+): connector admin (edit details, MCP, webhook).
// Aliased to avoid colliding with the store action method names of the same role.
import {
  editIntegrationDetails,
  manageMcp as apiManageMcp,
  resyncMcp as apiResyncMcp,
  disconnectMcp as apiDisconnectMcp,
  rotateWebhookSecret,
  getIntegrationDetail,
} from '../api/integrations';
import { listMcpTools } from '../api/connections';
import type { AuthMethod } from '../domain/types';
import { allInts, nameOf } from '../domain/catalog';
import { versionsFor, selectedVersionId } from '../domain/versions';
import { recommendedMethod, enabledMethods, METHOD_LABEL } from '../domain/auth';
import { unresolvedCount } from '../domain/mapping';
import { deriveName, detectCategory, randHex, initialsOf } from '../domain/naming';
import { ingestResult } from '../domain/ingest';
import { STEP_LABELS, INTS } from '../data/seeds';

const stamp = () =>
  new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

type Ctx = {
  state: State;
  // expose the actions object
  actions: ReturnType<typeof buildActions>;
};

const BridgeContext = createContext<Ctx | null>(null);

function buildActions(
  getState: () => State,
  dispatch: React.Dispatch<Action>,
  timers: { current: number[] },
) {
  const after = (ms: number, fn: () => void) => {
    const t = window.setTimeout(fn, ms);
    timers.current.push(t);
  };

  const toast = (msg: string, tone?: string) => {
    const id = getState()._tid + 1;
    dispatch({ type: 'TOAST', msg, tone });
    after(3400, () => dispatch({ type: 'DISMISS_TOAST', id }));
  };
  const patch = (p: Partial<State>) => dispatch({ type: 'PATCH', patch: p });
  const record = (id: string, msg: string, kind?: string) => dispatch({ type: 'RECORD', id, msg, kind, time: stamp() });
  const ints = () => allInts(getState().adapters, getState().metaOverrides);

  // --- FE↔BE integration helpers (Round 2+) ---
  // Resolve a connector slug → its BE integration_id by reversing slugById,
  // which the boot load populated. Returns undefined for connectors not known
  // to the backend (e.g. a freshly-ingested mock adapter not yet persisted).
  const integrationIdOf = (slug: string): string | undefined => {
    const map = getState().slugById;
    return Object.keys(map).find((iid) => map[iid] === slug);
  };
  // Patch the Manage-MCP drawer ONLY if it is still open for the same connector.
  // The real config/tool fetches are async; the user may close the drawer or open
  // another before they resolve, so we must not resurrect or cross-write state.
  const mcpManageSetIfOpen = (id: string, p: Partial<NonNullable<State['mcpManage']>>) => {
    const m = getState().mcpManage;
    if (m && m.id === id) patch({ mcpManage: { ...m, ...p } });
  };
  // Classify a real vendor tool name into the drawer's query/mutation label by
  // its leading verb. Heuristic only (display hint) — read verbs → query, the
  // rest → mutation. Vendor tool names look like 'ZohoCRM_getRecords'.
  const toMcpManageTools = (names: string[]): { name: string; kind: string }[] =>
    names.map((name) => {
      const verb = (name.split('_').pop() || name).toLowerCase();
      const isRead = /^(get|list|search|read|fetch|find|query|count|describe|execute)/.test(verb);
      return { name, kind: isRead ? 'query' : 'mutation' };
    });
  // After a connection mutation, re-pull conn/routes/connBySlug from the BE so
  // the directory reflects the new state. Failures are swallowed to a toast —
  // the optimistic CONN_SET already updated the pill.
  const refreshConn = async () => {
    try {
      const p = await refreshConnState(seedBindings());
      patch(p);
    } catch (e) {
      /* non-fatal: optimistic state already applied */
    }
  };

  const activeAdapter = () => {
    const s = getState();
    const fromState = s.adapters.find(a => a.id === s.activeAdapterId);
    if (fromState) return fromState;
    const int = ints().find(i => i.id === s.activeAdapterId);
    if (int)
      return {
        id: int.id, name: int.name, initials: int.initials, color: int.color, cls: int.cls,
        source: 'mapping · catalog v1', version: (int.versions && int.versions[0]) || '1.0',
        company: int.company, category: int.category, approved: true,
      };
    return s.adapters[0];
  };

  return {
    // ---- surface / role ----
    setSurface(v: State['surface']) {
      if (v === 'builder' && getState().role !== 'softknack') return toast('The Builder is Softknack-internal', 'warn');
      if (v === 'ops' && getState().role !== 'softknack') return toast('The ops surface is platform-ops only', 'warn');
      dispatch({ type: 'SET_SURFACE', surface: v });
    },
    setRole(r: State['role']) {
      dispatch({ type: 'SET_ROLE', role: r });
    },

    // ---- directory controls ----
    setQuery(q: string) { patch({ query: q }); },
    setFilter(f: State['filter']) { patch({ filter: f }); },
    setGroupBy(g: State['groupBy']) { patch({ groupBy: g }); },

    // ---- detail ----
    openDetail(id: string) { patch({ detailId: id }); },
    closeDetail() { patch({ detailId: null }); },

    // ---- connect ----
    openConnect(id: string) {
      const cfg = getState().authCfg[id];
      const int = ints().find(i => i.id === id);
      patch({ connectId: id, connectStep: 'idle', connectKey: '', connectMethod: recommendedMethod(cfg, int), detailId: null });
    },
    closeConnect() { patch({ connectId: null }); },
    setConnectMethod(m: AuthMethod) { patch({ connectMethod: m }); },
    setConnectKey(v: string) { patch({ connectKey: v }); },
    authorize(id: string) {
      patch({ connectStep: 'authorizing' });
      const intg = ints().find(i => i.id === id)!;
      const vendorVersion = (intg.versions && intg.versions[0]) || '1.0';
      // MCP-OAUTH PATH: for MCP connectors (Zoho/Stripe/HubSpot/…), connecting is
      // a real 3-legged OAuth: ask the BE for the vendor consent URL, open it in a
      // popup, and complete when the BE callback posts back success. Then refresh.
      if (USE_BACKEND && intg.cls === 'mcp') {
        const intId = integrationIdOf(id);
        if (!intId) { patch({ connectStep: 'idle' }); return toast('Connector not found on the backend', 'danger'); }
        startMcpOAuth(intId)
          .then(({ authorizationUrl }) => {
            // Open the vendor consent screen. A message listener (installed in
            // BridgeProvider) resolves the connect when the BE callback posts back.
            const popup = window.open(authorizationUrl, 'sk-oauth', 'width=520,height=720');
            if (!popup) { patch({ connectStep: 'idle' }); toast('Popup blocked — allow popups and retry', 'warn'); return; }
            // Stash which connector is mid-OAuth so the message handler knows.
            patch({ oauthPendingId: id });
          })
          .catch(err => { patch({ connectStep: 'idle' }); toast(err?.message || 'Could not start OAuth', 'danger'); });
        return;
      }
      // REST-OAUTH PATH: custom/openapi connectors (HubSpot's own REST API,
      // Approach B — SKBRIDGE-ARCH-001 D-1) also need a real 3-legged OAuth,
      // same popup+callback shape as the MCP path above, just a different
      // start endpoint. Without this branch these connectors fell through to
      // the generic createConnection call below, which stores a fake
      // placeholder credential instead of a real, refreshable token.
      if (USE_BACKEND && (intg.cls === 'custom' || intg.cls === 'openapi')) {
        const intId = integrationIdOf(id);
        if (!intId) { patch({ connectStep: 'idle' }); return toast('Connector not found on the backend', 'danger'); }
        startRestOAuth(intId)
          .then(({ authorizationUrl }) => {
            const popup = window.open(authorizationUrl, 'sk-oauth', 'width=520,height=720');
            if (!popup) { patch({ connectStep: 'idle' }); toast('Popup blocked — allow popups and retry', 'warn'); return; }
            patch({ oauthPendingId: id });
          })
          .catch(err => { patch({ connectStep: 'idle' }); toast(err?.message || 'Could not start OAuth', 'danger'); });
        return;
      }
      // BACKEND PATH: real POST /connections with auth_method=oauth. On success
      // we set the connection state from the BE response and refresh. On failure
      // we revert the step to 'idle' and surface the closed error message.
      if (USE_BACKEND) {
        const intId = integrationIdOf(id);
        if (!intId) { patch({ connectStep: 'idle' }); return toast('Connector not found on the backend', 'danger'); }
        createConnection(intId, vendorVersion, 'oauth')
          .then(res => {
            dispatch({ type: 'CONN_SET', id, conn: { connected: true, status: 'active', vendorVersion: res.vendor_version } });
            patch({ connectStep: 'done' });
            record(id, 'Tenant connected via OAuth', 'auth');
            toast('Connected to ' + intg.name, 'success');
            void refreshConn();
          })
          .catch(err => { patch({ connectStep: 'idle' }); toast(err?.message || 'Connect failed', 'danger'); });
        return;
      }
      // MOCK PATH (USE_BACKEND off): original simulated delay.
      after(1300, () => {
        dispatch({ type: 'CONN_SET', id, conn: { connected: true, status: 'active', vendorVersion } });
        patch({ connectStep: 'done' });
        record(id, 'Tenant connected via OAuth', 'auth');
        toast('Connected to ' + intg.name, 'success');
      });
    },
    submitConnectKey(id: string) {
      const key = (getState().connectKey || '').trim();
      if (!key) return toast('Paste your credential first', 'warn');
      const method = getState().connectMethod;
      patch({ connectStep: 'authorizing' });
      const intg = ints().find(i => i.id === id)!;
      const vendorVersion = (intg.versions && intg.versions[0]) || '1.0';
      const verb = method === 'bearer' ? 'Bearer token' : 'API key';
      // BACKEND PATH: real POST /connections with the chosen apikey/bearer method.
      if (USE_BACKEND) {
        const intId = integrationIdOf(id);
        if (!intId) { patch({ connectStep: 'idle' }); return toast('Connector not found on the backend', 'danger'); }
        createConnection(intId, vendorVersion, method, key)
          .then(res => {
            dispatch({ type: 'CONN_SET', id, conn: { connected: true, status: 'active', vendorVersion: res.vendor_version } });
            patch({ connectStep: 'done', connectKey: '' });
            record(id, 'Tenant connected via ' + verb + ' (stored by reference)', 'auth');
            toast('Connected to ' + intg.name, 'success');
            void refreshConn();
          })
          .catch(err => { patch({ connectStep: 'idle' }); toast(err?.message || 'Connect failed', 'danger'); });
        return;
      }
      // MOCK PATH.
      after(900, () => {
        dispatch({ type: 'CONN_SET', id, conn: { connected: true, status: 'active', vendorVersion } });
        patch({ connectStep: 'done', connectKey: '' });
        record(id, 'Tenant connected via ' + verb + ' (stored by reference)', 'auth');
        toast('Connected to ' + intg.name, 'success');
      });
    },
    finishConnect(id: string) { patch({ connectId: null, grantId: id }); },

    // MCP-OAuth: called when the consent popup's BE callback posts back. On
    // success we re-pull connection state (the BE created/activated it) and mark
    // the connector connected; on failure we reset the connect step.
    finishOAuthConnect(ok: boolean) {
      const id = getState().oauthPendingId;
      patch({ oauthPendingId: null });
      if (!ok) { patch({ connectStep: 'idle' }); return toast('Authorization failed or cancelled', 'warn'); }
      if (id) {
        const intg = ints().find(i => i.id === id);
        dispatch({ type: 'CONN_SET', id, conn: { connected: true, status: 'active', vendorVersion: 'mcp' } });
        patch({ connectStep: 'done' });
        record(id, 'Connected via MCP OAuth', 'auth');
        toast('Connected to ' + (intg?.name || id), 'success');
      }
      void refreshConn();
    },

    // ---- grant / routes ----
    openGrant(id: string) { patch({ grantId: id, detailId: null }); },
    closeGrant() { patch({ grantId: null }); },
    toggleGrant(intId: string, product: string, dataType: string, on: boolean) {
      const key = product + ':' + dataType;
      // BACKEND PATH (Round 3): translate the FE binding to its canonical route
      // and call the real grant endpoints. The FE product label is display-only;
      // we send the canonical owner code (mapBinding) the BE requires.
      if (USE_BACKEND) {
        const canonical = mapBinding({ key: product, dataType });
        // Unmappable binding (messaging/payments/tickets) — no canonical entity
        // in catalog v1. The drawer disables these, so this is a defensive guard.
        if (!canonical) {
          return toast(product + ' · ' + dataType + ' isn’t in catalog v1 yet', 'warn');
        }
        if (on) {
          // Turning OFF → revoke the existing grant for this route.
          const grantId = getState().grantIdByRoute[key];
          if (!grantId) { void refreshConn(); return; }
          revokeGrant(grantId)
            .then(() => {
              dispatch({ type: 'TOGGLE_GRANT', intId, product, dataType, on });
              toast(product + ' can no longer use ' + nameOf(intId, ints()), 'info');
              void refreshConn();
            })
            .catch(err => toast(err?.message || 'Revoke failed', 'danger'));
          return;
        }
        // Turning ON → create the grant. Needs the connector's connection_id.
        const connId = getState().connBySlug[intId];
        if (!connId) return toast('Connect ' + nameOf(intId, ints()) + ' first', 'warn');
        createGrant(connId, canonical.productKey, canonical.dataType)
          .then(() => {
            dispatch({ type: 'TOGGLE_GRANT', intId, product, dataType, on });
            toast(product + ' can now use ' + nameOf(intId, ints()) + ' for ' + dataType, 'success');
            void refreshConn();
          })
          .catch(err => {
            // The BE's single-active-route conflict → open the FE "switch?" modal.
            if (err instanceof SingleActiveRouteError) {
              const existing = getState().routes[key];
              patch({ replace: { product, dataType, fromId: existing || '', toId: intId } });
              return;
            }
            toast(err?.message || 'Grant failed', 'danger');
          });
        return;
      }
      // MOCK PATH (unchanged original behavior).
      const before = getState().routes;
      dispatch({ type: 'TOGGLE_GRANT', intId, product, dataType, on });
      if (on) return toast(product + ' can no longer use ' + nameOf(intId, ints()), 'info');
      const existing = before[key];
      if (existing && existing !== intId) return; // replace modal opened
      toast(product + ' can now use ' + nameOf(intId, ints()) + ' for ' + dataType, 'success');
    },
    confirmReplace() {
      const r = getState().replace;
      if (!r) return;
      // BACKEND PATH: re-issue the grant with replace=true to switch the owner.
      if (USE_BACKEND) {
        const canonical = mapBinding({ key: r.product, dataType: r.dataType });
        const connId = getState().connBySlug[r.toId];
        if (!canonical || !connId) { patch({ replace: null }); return; }
        createGrant(connId, canonical.productKey, canonical.dataType, true)
          .then(() => {
            dispatch({ type: 'CONFIRM_REPLACE' });
            toast(r.product + ' · ' + r.dataType + ' switched to ' + nameOf(r.toId, ints()), 'success');
            void refreshConn();
          })
          .catch(err => { patch({ replace: null }); toast(err?.message || 'Switch failed', 'danger'); });
        return;
      }
      // MOCK PATH.
      dispatch({ type: 'CONFIRM_REPLACE' });
      toast(r.product + ' · ' + r.dataType + ' switched to ' + nameOf(r.toId, ints()), 'success');
    },
    cancelReplace() { patch({ replace: null }); },

    // ---- reauth ----
    reauth(id: string) {
      const c = getState().conn[id];
      // BACKEND PATH: POST /connections/:id/reauthorize for the connection bound
      // to this connector slug. Re-pull state after so an expired→active flip
      // (and any vendor_version change) is reflected.
      if (USE_BACKEND) {
        const connId = getState().connBySlug[id];
        if (!connId) return toast('No connection to re-authorize', 'warn');
        reauthorizeConnection(connId)
          .then(res => {
            dispatch({ type: 'CONN_SET', id, conn: { connected: true, status: 'active', vendorVersion: res.vendor_version } });
            record(id, 'Re-authorized · OAuth token refreshed', 'auth');
            toast('Re-authorized ' + nameOf(id, ints()), 'success');
            void refreshConn();
          })
          .catch(err => toast(err?.message || 'Re-authorize failed', 'danger'));
        return;
      }
      // MOCK PATH.
      dispatch({ type: 'CONN_SET', id, conn: { ...c, status: 'active' } });
      record(id, 'Re-authorized · OAuth token refreshed', 'auth');
      toast('Re-authorized ' + nameOf(id, ints()), 'success');
    },

    // ---- revoke (FE↔BE integration) ----
    // The detail modal exposes revoke for an active connection. Mock builds had
    // no revoke action wired; we add one that calls POST /connections/:id/revoke
    // when on the backend, and optimistically flips the pill to Available.
    revoke(id: string) {
      if (USE_BACKEND) {
        const connId = getState().connBySlug[id];
        if (!connId) return toast('No connection to revoke', 'warn');
        revokeConnection(connId)
          .then(() => {
            dispatch({ type: 'CONN_SET', id, conn: { connected: false, status: 'revoked', vendorVersion: null } });
            record(id, 'Connection revoked', 'auth');
            toast('Revoked ' + nameOf(id, ints()), 'info');
            void refreshConn();
          })
          .catch(err => toast(err?.message || 'Revoke failed', 'danger'));
        return;
      }
      dispatch({ type: 'CONN_SET', id, conn: { connected: false, status: 'revoked', vendorVersion: null } });
      record(id, 'Connection revoked', 'auth');
      toast('Revoked ' + nameOf(id, ints()), 'info');
    },

    // ---- builder: adapter / version / direction / tabs ----
    switchAdapter(id: string) { dispatch({ type: 'SWITCH_ADAPTER', id }); },
    pickAdapter(val: string) {
      if (val === '__new__') return this.openIngest();
      dispatch({ type: 'SWITCH_ADAPTER', id: val });
    },
    selectVersion(vid: string) { dispatch({ type: 'SET_VERSION', id: getState().activeAdapterId, versionId: vid }); },
    setDirection(d: State['direction']) { patch({ direction: d, selectedNode: null }); },
    setRightTab(t: State['rightTab']) { patch({ rightTab: t }); },
    editInBuilder(id: string) {
      if (getState().role !== 'softknack') return toast('Only Softknack can edit mappings', 'warn');
      const int = ints().find(i => i.id === id);
      if (!int) return;
      const s = getState();
      let adapters = s.adapters;
      if (!adapters.some(a => a.id === id)) {
        const initials = int.initials || initialsOf(int.name);
        adapters = [...adapters, { id: int.id, name: int.name, initials, color: int.color, cls: int.cls, source: 'mapping · catalog v1', version: (int.versions && int.versions[0]) || '1.0' }];
      }
      patch({
        adapters, activeAdapterId: id, surface: 'builder', detailId: null, direction: 'outbound', rightTab: 'auth',
        selectedNode: null, resolutions: { subject: null, name: null }, approved: false,
        entityMapping: null, entityMappingError: null,
      });
      toast('Opened ' + int.name + ' mapping in the Builder', 'info');
      void this.loadEntityMapping(id);
    },

    // Real, live per-field candidate mapping (AI Integration Builder canvas).
    // Only fetches for a connector that's actually connected to the backend —
    // no real connection means there's nothing real to show, and useBuilder
    // falls back to the seeds.ts mock scenario in that case (never silently
    // blending real candidates with mock ones).
    async loadEntityMapping(id: string, entityKey = 'Party') {
      if (!USE_BACKEND) return;
      const connId = getState().connBySlug[id];
      if (!connId) return; // not connected on the backend — nothing real to load
      patch({ entityMappingLoading: true, entityMappingError: null });
      try {
        const res = await autoMapEntity(connId, entityKey);
        if (getState().activeAdapterId !== id) return; // user navigated away mid-fetch
        patch({ entityMapping: res.draft, entityMappingLoading: false });
      } catch (err) {
        if (getState().activeAdapterId !== id) return;
        const msg = err instanceof Error ? err.message : 'Failed to load mapping';
        patch({ entityMappingLoading: false, entityMappingError: msg });
        toast(msg, 'danger');
      }
    },

    // ---- field mapping ----
    toggleField(fid: string) { dispatch({ type: 'TOGGLE_FIELD', fid }); },
    setFieldSearch(v: string) { patch({ fieldSearch: v }); },
    pickTarget(fid: string, disc: string | undefined, label: string, opts?: { manual?: boolean }) {
      const remembered = disc && getState().remember[disc as 'subject' | 'name'];
      dispatch({ type: 'PICK_TARGET', fid, disc, label });
      const verb = opts?.manual ? 'mapped to' : disc ? '→' : 'changed to';
      toast(verb + ' ' + label + (remembered ? ' · remembered for ' + activeAdapter().name : ''), 'success');
    },
    resetField(fid: string, disc?: string) {
      dispatch({ type: 'RESET_FIELD', fid, disc });
      toast('Reverted to the AI suggestion', 'info');
    },
    toggleRemember(disc: 'subject' | 'name') { dispatch({ type: 'TOGGLE_REMEMBER', disc }); },

    // ---- auth panel ----
    // FE↔BE integration NOTE (Round 7): the auth-method toggles + recommended +
    // api-key header name remain FE-only. The BE PATCH /integrations/:id schema
    // (editDetailsSchema) does NOT accept allowed_auth_methods / recommended_auth_
    // method / api_key_header_name, and there is no other endpoint to persist
    // them. Known BE gap — these settings stay in local state for now (the boot
    // loader still SEEDS authCfg from the BE so the connect modal is correct).
    toggleAuthMethod(id: string, m: AuthMethod) {
      const int = ints().find(i => i.id === id);
      const cur = enabledMethods(getState().authCfg[id], int);
      const has = cur.includes(m);
      if (has && cur.length === 1) return toast('At least one method must stay enabled', 'warn');
      dispatch({ type: 'TOGGLE_AUTH_METHOD', id, method: m, time: stamp() });
      toast(METHOD_LABEL[m] + (has ? ' disabled' : ' enabled'), 'info');
    },
    setRecommendedMethod(id: string, m: AuthMethod) {
      const int = ints().find(i => i.id === id);
      if (!enabledMethods(getState().authCfg[id], int).includes(m)) return;
      dispatch({ type: 'SET_RECOMMENDED', id, method: m });
      toast(METHOD_LABEL[m] + ' set as recommended', 'success');
    },
    authSetHeader(id: string, headerName: string) { dispatch({ type: 'AUTH_SET', id, patch: { headerName } }); },

    // ---- change delivery ----
    setChangeDelivery(v: State['changeDelivery']) { patch({ changeDelivery: v }); toast('Change-delivery set to ' + v, 'info'); },
    setPollInterval(v: State['pollInterval']) { patch({ pollInterval: v }); toast('Poll interval · every ' + v + ' min', 'info'); },
    regenSecret(id: string) {
      // BACKEND PATH (Round 7): POST /webhook/rotate-secret. The BE mints the
      // secret, stores it by reference, and returns the raw value ONCE — we show
      // exactly that real value (not a locally-generated placeholder).
      if (USE_BACKEND) {
        const intId = integrationIdOf(id);
        if (!intId) return toast('Connector not on backend', 'warn');
        rotateWebhookSecret(intId)
          .then(res => {
            dispatch({ type: 'WEBHOOK_SET', id, patch: { secret: res.secret, verified: false } });
            record(id, 'Webhook signing secret rotated', 'config');
            toast('Signing secret rotated', 'success');
          })
          .catch(err => toast(err?.message || 'Rotate failed', 'danger'));
        return;
      }
      // MOCK PATH.
      dispatch({ type: 'WEBHOOK_SET', id, patch: { secret: 'whsec_' + randHex(28), verified: false } });
      record(id, 'Webhook signing secret rotated', 'config');
      toast('Signing secret rotated', 'success');
    },
    testWebhook(id: string) {
      const ev = (getState().webhookCfg[id] || {}).events || [];
      dispatch({ type: 'WEBHOOK_SET', id, patch: { verified: true, events: [{ type: 'ping.test', ago: 'just now' }, ...ev].slice(0, 4) } });
      record(id, 'Inbound webhook test received & verified', 'config');
      toast('Test event received · signature verified', 'success');
    },
    copyText(t: string, msg?: string) {
      try { navigator.clipboard && navigator.clipboard.writeText(t); } catch (e) {}
      toast(msg || 'Copied', 'info');
    },

    // ---- outbound ----
    setOutDraftUrl(v: string) { patch({ outDraftUrl: v }); },
    toggleDraftEvent(ev: string) { dispatch({ type: 'TOGGLE_DRAFT_EVENT', ev }); },
    addOutbound(id: string) {
      const url = getState().outDraftUrl.trim();
      if (!/^https?:\/\//.test(url)) return toast('Enter a valid https:// URL', 'warn');
      const evs = getState().outDraftEvents.length ? getState().outDraftEvents : ['created'];
      const hook = { id: 'oh-' + Date.now(), url, events: evs.slice(), secret: 'whsk_' + randHex(24), active: true };
      dispatch({ type: 'ADD_OUTBOUND', id, hook, time: stamp() });
      toast('Outbound webhook added', 'success');
    },
    toggleOutbound(id: string, hid: string) { dispatch({ type: 'TOGGLE_OUTBOUND', id, hid }); },
    removeOutbound(id: string, hid: string) {
      dispatch({ type: 'REMOVE_OUTBOUND', id, hid, time: stamp() });
      toast('Outbound webhook removed', 'info');
    },

    // ---- approve ----
    openApprove() {
      const s = getState();
      // Real data (entityMapping loaded): "unresolved" means a canonical field
      // the AI found ZERO real candidates for, minus any the user has since
      // picked an override for — NOT the mock's fixed subject/name concept,
      // which stays null forever for a real connector and would always
      // wrongly report "2 unresolved" regardless of the actual mapping.
      const u = s.entityMapping
        ? s.entityMapping.fields.filter(f => f.candidates.length === 0 && !s.overrides[`outbound:${f.canonicalField}`] && !s.overrides[`inbound:${f.canonicalField}`]).length
        : unresolvedCount(s.resolutions);
      if (u > 0) return toast('Resolve ' + u + ' field(s) before approving', 'warn');
      patch({ approveOpen: true });
    },
    closeApprove() { patch({ approveOpen: false }); },
    approve() {
      const s = getState();
      const id = s.activeAdapterId;
      const selId = selectedVersionId(id, s.versions, s.adapters, s.versionSel);
      const curVs = versionsFor(id, s.versions, s.adapters);
      const selVendor = (curVs.find(v => v.id === selId) || ({} as any)).vendor;
      const newRev = ((curVs.find(v => v.id === selId) || ({} as any)).rev || 0) + 1;
      const name = activeAdapter().name;
      const localApprove = () => {
        dispatch({ type: 'APPROVE', time: stamp() });
        toast('Approved by ' + s.currentUser.name + ' · ' + name + ' v1 × ' + selVendor + ' · rev ' + newRev + ' is now live', 'success');
      };
      // REAL FIELD-MAPPING PATH: when the canvas is showing real per-field
      // candidates (entityMapping loaded), approve MUST persist through
      // entity-mapping/approve — the version/tool-level approveVersion() below
      // has no operations to publish for a connector mapped this way (BE seeds
      // ship zero bridge_operation rows; they only exist after this call), so
      // it would silently no-op into the local demo path and nothing would be
      // saved. This is the one action that actually writes bridge_operation
      // rows and publishes a live version for the entity-mapping flow.
      if (USE_BACKEND && s.entityMapping) {
        const connId = s.connBySlug[id];
        const vendorVersion = (s.conn[id] && s.conn[id].vendorVersion) || selVendor;
        if (!connId) {
          return toast('Connect this connector before approving a mapping — there is nothing to save it against', 'warn');
        }
        const fields = s.entityMapping.fields
          .map(f => {
            const fid = `${s.direction}:${f.canonicalField}`;
            const override = s.overrides[fid];
            const top = f.candidates[0];
            const vendorField = override || (top ? top.vendorField : null);
            return vendorField ? { canonicalField: f.canonicalField, vendorField } : null;
          })
          .filter((f): f is { canonicalField: string; vendorField: string } => !!f);
        if (fields.length === 0) {
          return toast('Nothing to approve — no field has a chosen vendor field yet', 'warn');
        }
        approveEntityMapping(connId, s.entityMapping.entityKey, s.direction, vendorVersion, fields)
          .then(res => {
            // Local state only AFTER the backend confirmed the write.
            localApprove();
            toast('Published ' + name + ' v1 × ' + vendorVersion + ' · ' + res.operation_count + ' field(s) mapped on the backend', 'success');
          })
          .catch(err => toast(err?.message || 'Approve failed', 'danger'));
        return;
      }
      // BACKEND PATH: approve = publish the connector's DRAFT version on the
      // backend. The FE's selId is a synthesized "slug|vendor" string, not the
      // BE uuid, so the real version_id is resolved by listing BE versions.
      //
      // Every branch here used to fall back to localApprove() — including the
      // catch — so a failed or impossible publish still rendered "…is now live".
      // The user was told their mapping shipped when nothing had been written.
      // In backend mode this now reports what actually happened, and only
      // mutates local state after the backend confirms.
      if (USE_BACKEND) {
        const intId = integrationIdOf(id);
        if (!intId) {
          return toast('This connector does not exist on the backend yet — nothing to publish', 'warn');
        }
        listVersions(intId)
          .then(versions => {
            const draft = versions.find(v => v.status === 'draft');
            if (!draft) {
              return toast('No draft version to publish — map fields first, then approve', 'warn');
            }
            return approveVersion(draft.version_id).then(() => {
              localApprove();
              toast('Published ' + name + ' v1 × ' + draft.vendor_version + ' on the backend', 'success');
            });
          })
          .catch(err => toast('Approve failed · ' + (err?.message || 'backend unavailable'), 'danger'));
        return;
      }
      // MOCK PATH.
      localApprove();
    },

    // ---- meta manage ----
    openMetaManage(id: string) {
      if (getState().role !== 'softknack') return toast('Only Softknack can edit connectors', 'warn');
      const a = ints().find(i => i.id === id) || ({} as any);
      patch({ metaManage: { id, name: a.name || '', category: a.category || 'CRM', change: a.change || 'poll', purpose: a.purpose || '' }, detailId: null });
    },
    closeMetaManage() { const m = getState().metaManage; patch({ metaManage: null, detailId: m ? m.id : null }); },
    metaManageSet(p: Partial<NonNullable<State['metaManage']>>) { const m = getState().metaManage; if (m) patch({ metaManage: { ...m, ...p } }); },
    saveMetaManage() {
      const m = getState().metaManage!;
      if (!m.name.trim()) return toast('Name can’t be empty', 'warn');
      const dupe = ints().find(i => i.id !== m.id && i.name.toLowerCase() === m.name.trim().toLowerCase());
      if (dupe) return toast('Another connector is already named ' + m.name.trim(), 'warn');
      const over = { name: m.name.trim(), company: m.name.trim(), category: m.category, change: m.change, purpose: m.purpose };
      // BACKEND PATH (Round 5): PATCH /integrations/:id with the edited fields.
      // The FE keeps applying the optimistic SAVE_META (so metaOverrides + the
      // internal-rev bump still happen locally); the PATCH persists the change.
      if (USE_BACKEND) {
        const intId = integrationIdOf(m.id);
        if (!intId) { dispatch({ type: 'SAVE_META', id: m.id, time: stamp(), over }); return toast('Saved locally (connector not on backend)', 'warn'); }
        editIntegrationDetails(intId, {
          display_name: m.name.trim(),
          category: m.category,
          // FE `change` is webhook|poll; BE also accepts none. Passed straight through.
          change_delivery: m.change,
          purpose: m.purpose,
        })
          .then(() => {
            dispatch({ type: 'SAVE_META', id: m.id, time: stamp(), over });
            toast(m.name.trim() + ' · details updated', 'success');
          })
          .catch(err => toast(err?.message || 'Save failed', 'danger'));
        return;
      }
      // MOCK PATH.
      dispatch({ type: 'SAVE_META', id: m.id, time: stamp(), over });
      toast(m.name.trim() + ' · details updated · internal rev bumped', 'success');
    },

    // ---- mcp manage ----
    openMcpManage(id: string) {
      if (getState().role !== 'softknack') return toast('Only Softknack can manage adapters', 'warn');
      const a = (ints().find(i => i.id === id) || {}) as any;
      const mo = (getState().mcpOverrides || {})[id] || {};
      // Open immediately with whatever we have (overrides or, as a last resort,
      // the legacy placeholder). On the backend path we then REPLACE url/transport/
      // tools with the real values fetched below — so a connected server never
      // shows the fabricated `mcp.<vendor>.com/sse` placeholder or mock tools.
      patch({
        mcpManage: {
          id,
          url: mo.url || a._mcpUrl || 'https://mcp.' + String(a.company || a.name || 'vendor').toLowerCase().replace(/[^a-z0-9]/g, '') + '.com/sse',
          transport: mo.transport || a._transport || 'sse',
          category: a.category || 'CRM',
          resyncing: false,
          extraTool: mo.extraTool || a._extraTool || false,
          loading: USE_BACKEND,
        },
        detailId: null,
      });

      if (!USE_BACKEND) return; // mock/demo connector: keep the seeded view.

      const intId = integrationIdOf(id);
      const connId = getState().connBySlug[id];
      if (!intId) { mcpManageSetIfOpen(id, { loading: false }); return; }

      // 1) Real server_url + transport from the integration's bridge_mcp_config.
      //    BE transport enum ('streamable_http') maps back to the FE token 'stdio'
      //    (the UI labels 'stdio' as "streamable-http"); 'sse' stays 'sse'.
      getIntegrationDetail(intId)
        .then(det => {
          // `managed` locks Server URL + Transport for built-in connectors
          // (HubSpot/Zoho): their URL is owned by the connector definition, not
          // the tenant. Apply managed + url/transport in ONE patch: two separate
          // mcpManageSetIfOpen calls both read the same pre-render `m` snapshot
          // (getState() returns stateRef.current, which only updates on re-render),
          // so the second would clobber the first — dropping `managed`.
          const upd: Partial<NonNullable<State['mcpManage']>> = { managed: !!det.managed };
          if (det.mcp) {
            upd.url = det.mcp.server_url;
            upd.transport = det.mcp.transport === 'streamable_http' ? 'stdio' : 'sse';
          }
          mcpManageSetIfOpen(id, upd);
        })
        .catch(() => undefined); // non-fatal: leave the existing url/transport

      // 2) Real tool list from the LIVE server (only possible with a connection).
      if (connId) {
        listMcpTools(connId)
          .then(res => mcpManageSetIfOpen(id, { tools: toMcpManageTools(res.tools), loading: false }))
          .catch(err => { mcpManageSetIfOpen(id, { loading: false }); toast(err?.message || 'Could not list MCP tools', 'danger'); });
      } else {
        mcpManageSetIfOpen(id, { loading: false });
      }
    },
    closeMcpManage() { const m = getState().mcpManage; patch({ mcpManage: null, detailId: m ? m.id : null }); },

    // Mapping Plane: open/close the "Map with AI" modal for a connector. The
    // modal itself drives the BE auto/approve calls; the store only tracks which
    // connector is open (and resolves its connection_id from connBySlug).
    openMapAi(id: string) {
      if (getState().role !== 'softknack') return toast('Only Softknack can map adapters', 'warn');
      patch({ mapAiId: id, mcpManage: null });
    },
    closeMapAi() { patch({ mapAiId: null }); },
    mcpManageSet(p: Partial<NonNullable<State['mcpManage']>>) { const m = getState().mcpManage; if (m) patch({ mcpManage: { ...m, ...p } }); },
    resyncMcp() {
      const m = getState().mcpManage; if (!m || m.resyncing) return;
      patch({ mcpManage: { ...m, resyncing: true } });
      // BACKEND PATH: re-discover the REAL tools from the live server, then
      // persist them via POST /mcp/resync (which flags genuinely-new names vs.
      // what was last stored), and display the live list. Previously this sent an
      // empty list and only toggled a mock `bulk_export` flag — so it never showed
      // real tools. Order matters: list live → resync(persist+diff) → display.
      if (USE_BACKEND) {
        const intId = integrationIdOf(m.id);
        const connId = getState().connBySlug[m.id];
        if (!intId) { patch({ mcpManage: { ...m, resyncing: false } }); return toast('Connector not on backend', 'warn'); }
        if (!connId) { patch({ mcpManage: { ...m, resyncing: false } }); return toast('Connect the server first to list tools', 'warn'); }
        listMcpTools(connId)
          .then(async (live) => {
            // Persist the freshly-discovered names so the BE can flag new ones.
            const res = await apiResyncMcp(intId, live.tools).catch(() => ({ tools: live.tools, new_tools: [] as string[] }));
            const newSet = new Set(res.new_tools);
            const tools = toMcpManageTools(live.tools).map(t => ({ ...t, isNew: newSet.has(t.name) }));
            mcpManageSetIfOpen(m.id, { resyncing: false, tools, extraTool: res.new_tools.length > 0 });
            toast('Tools re-synced · ' + live.tools.length + ' tools' + (res.new_tools.length ? ' · ' + res.new_tools.length + ' new' : ''), 'success');
          })
          .catch(err => { mcpManageSetIfOpen(m.id, { resyncing: false }); toast(err?.message || 'Re-sync failed', 'danger'); });
        return;
      }
      // MOCK PATH.
      after(1200, () => { const mm = getState().mcpManage; if (mm) patch({ mcpManage: { ...mm, resyncing: false, extraTool: true } }); });
    },
    saveMcpManage() {
      const m = getState().mcpManage!;
      const commit = () => {
        dispatch({ type: 'SAVE_MCP', id: m.id, category: m.category, extraTool: m.extraTool, over: { url: m.url, transport: m.transport, extraTool: m.extraTool }, time: stamp() });
        toast(nameOf(m.id, ints()) + ' · MCP connection updated', 'success');
      };
      // BACKEND PATH (Round 6): POST /integrations/:id/mcp to wire/replace the
      // server (transport mapped FE→BE inside manageMcp). The local SAVE_MCP
      // still runs so the modal's category/override state stays consistent.
      if (USE_BACKEND) {
        const intId = integrationIdOf(m.id);
        if (!intId) { commit(); return; }
        apiManageMcp(intId, m.url, m.transport)
          .then(commit)
          .catch(err => toast(err?.message || 'MCP update failed', 'danger'));
        return;
      }
      // MOCK PATH.
      commit();
    },
    disconnectMcp() {
      const m = getState().mcpManage!;
      const name = nameOf(m.id, ints());
      // BACKEND PATH (Round 6): DELETE /integrations/:id/mcp removes the MCP
      // config (the integration row remains). The local DISCONNECT_MCP only
      // affects user-ingested adapters; built-ins (in INTS) are untouched.
      if (USE_BACKEND) {
        const intId = integrationIdOf(m.id);
        if (intId) {
          apiDisconnectMcp(intId).catch(err => toast(err?.message || 'Disconnect failed', 'danger'));
        }
      }
      dispatch({ type: 'DISCONNECT_MCP', id: m.id });
      // Accurate wording: this removed the MCP server wiring, not the connection.
      // True disconnect (revoke) is a separate action in the detail modal.
      toast(name + ' · MCP config removed', 'info');
    },

    // ---- logs ----
    openLogs(id: string) {
      patch({ logsId: id, logsTab: 'exec', detailId: null });
      // Round 4: fetch real observability data (stats/executions/audit) for the
      // connector's BE integration and stash it under state.obs[id]. The modal
      // prefers this when present; failures leave obs unset → synthesized fallback.
      if (USE_BACKEND) {
        const intId = integrationIdOf(id);
        if (!intId) return;
        loadObservability(intId)
          .then(data => patch({ obs: { ...getState().obs, [id]: data } }))
          .catch(() => { /* non-fatal: modal falls back to synthesized rows */ });
      }
    },
    closeLogs() { const id = getState().logsId; patch({ logsId: null, detailId: id }); },
    setLogsTab(t: State['logsTab']) { patch({ logsTab: t }); },

    // ---- ingest wizard ----
    openIngest() {
      if (getState().role !== 'softknack') return toast('Only Softknack can add integrations', 'warn');
      patch({ ingest: { step: 'kind', sourceType: 'openapi', url: '', progress: 0, result: null, category: null, categoryTouched: false, mcpUrl: '', mcpTransport: 'sse' } });
    },
    closeIngest() { patch({ ingest: null }); },
    chooseKind(k: 'mcp' | 'api') { const ig = getState().ingest!; patch({ ingest: { ...ig, step: k === 'mcp' ? 'mcp' : 'source' } }); },
    backToKind() { const ig = getState().ingest!; patch({ ingest: { ...ig, step: 'kind' } }); },
    onMcpUrl(v: string) { const ig = getState().ingest!; patch({ ingest: { ...ig, mcpUrl: v } }); },
    setMcpTransport(t: 'sse' | 'stdio') { const ig = getState().ingest!; patch({ ingest: { ...ig, mcpTransport: t } }); },
    connectMcp() {
      const ig = getState().ingest!;
      if (!(ig.mcpUrl || '').trim()) return toast('Enter the MCP server URL', 'warn');
      const name = deriveName(ig.mcpUrl || '');
      const existing = ints().find(i => i.name.toLowerCase() === name.toLowerCase());
      if (existing) return toast(name + ' is already an integration', 'warn');
      patch({ ingest: { ...ig, step: 'mcpDiscover', mcpName: name } });
      after(1400, () => { const s2 = getState(); if (s2.ingest) patch({ ingest: { ...s2.ingest, step: 'mcpDone' } }); });
    },
    addMcp() {
      const ig = getState().ingest!;
      const name = ig.mcpName || deriveName(ig.mcpUrl || '');
      const initials = initialsOf(name) || 'MC';
      const color = (require('../data/seeds').COLORS as string[])[getState().adapters.length % 6];
      const cat = ig.categoryTouched ? ig.category : detectCategory(ig.mcpUrl || '');
      const a = {
        id: 'mcp-' + Date.now(), name, initials, color, cls: 'mcp' as const, source: 'MCP server',
        version: '1.0', purpose: 'MCP server — tools invoked natively over ' + ig.mcpTransport + '.',
        products: [{ key: 'CRM', dataType: 'contacts' }, { key: 'Hire', dataType: 'contacts' }],
        change: 'webhook' as const, company: name, category: cat || 'Other', inDirectory: true, approved: true,
      };
      patch({ ingest: null, adapters: [...getState().adapters, a] });
      toast(name + ' added to Connectors · MCP, available to connect', 'success');
    },
    setIngestSource(t: 'openapi' | 'prose') { const ig = getState().ingest!; patch({ ingest: { ...ig, sourceType: t, url: '' } }); },
    onIngestUrl(v: string) { const ig = getState().ingest!; patch({ ingest: { ...ig, url: v } }); },
    onIngestCategory(v: string) { const ig = getState().ingest!; patch({ ingest: { ...ig, category: v, categoryTouched: true } }); },
    onVersionVendor(v: string) { const ig = getState().ingest!; patch({ ingest: { ...ig, versionVendor: v } }); },
    fakeUpload() { const ig = getState().ingest!; patch({ ingest: { ...ig, url: 'openapi.json (uploaded · 412 paths)' } }); toast('Spec uploaded · 412 paths parsed', 'success'); },
    openAddVersion() {
      const a = activeAdapter();
      const st = a.cls === 'openapi' ? 'openapi' : 'prose';
      patch({ ingest: { step: 'source', mode: 'version', targetAdapterId: a.id, sourceType: st as 'openapi' | 'prose', url: '', versionVendor: '', progress: 0, result: null } });
    },
    beginIngest() {
      const ig = getState().ingest!;
      if (!ig.url.trim()) return toast('Provide a source URL or upload a spec', 'warn');
      if (ig.mode === 'version' && !(ig.versionVendor || '').trim()) return toast('Enter the new vendor version number', 'warn');
      const name = ig.mode === 'version' ? nameOf(ig.targetAdapterId!, ints()) : deriveName(ig.url);
      if (ig.mode !== 'version') {
        const existing = ints().find(i => i.name.toLowerCase() === name.toLowerCase());
        if (existing) { patch({ ingest: { ...ig, step: 'duplicate', detectedName: name, dupAdapterId: existing.id, versionVendor: '' } }); return; }
      }
      patch({ ingest: { ...ig, step: 'ingesting', progress: 0, detectedName: name } });
      const tick = () => {
        const s2 = getState();
        if (!s2.ingest) return;
        const p = s2.ingest.progress + 1;
        if (p > 4) {
          const r = ingestResult(s2.ingest.sourceType, s2.ingest.url, s2.ingest.detectedName, s2.ingest.category || undefined, s2.adapters.length);
          patch({ ingest: { ...s2.ingest, step: 'done', progress: 4, result: r } });
          // BACKEND PATH (Round 9): in parallel with the local mapping preview,
          // fire a real POST /builder/ingest so a genuine DRAFT version is
          // created on the backend. The FE keeps its rich synthesized mapping UI
          // (the BE parser output shape differs); the backend draft is what the
          // subsequent approve() publishes. Fire-and-forget — failure just means
          // the demo stays local.
          if (USE_BACKEND && s2.ingest.mode !== 'version') {
            const detected = s2.ingest.detectedName || name;
            const slug = detected.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            // Map the FE source kind to the BE ingest source shape.
            const source = s2.ingest.sourceType === 'openapi'
              ? { openapi: { info: { title: detected } }, change_delivery: 'webhook' as const }
              : { prose: 'Ingested from ' + s2.ingest.url, change_delivery: 'webhook' as const };
            ingestVendor(slug, detected, source, s2.ingest.versionVendor || 'v1')
              .then(res => {
                const cur = getState().ingest;
                if (cur && cur.result) patch({ ingest: { ...cur, result: { ...cur.result, _beVersionId: res.version_id } as any } });
              })
              .catch(() => { /* demo stays local */ });
          }
          return;
        }
        patch({ ingest: { ...s2.ingest, progress: p } });
        after(520, tick);
      };
      after(520, tick);
    },
    openMapping() {
      const ig = getState().ingest!;
      if (ig.mode === 'version') return this.finishNewVersion();
      const a = { ...ig.result!.adapter, inDirectory: true, approved: false };
      const s = getState();
      patch({
        ingest: null,
        adapters: s.adapters.some(x => x.id === a.id) ? s.adapters : [...s.adapters, a],
        activeAdapterId: a.id, surface: 'builder', direction: 'outbound', rightTab: 'auth',
        resolutions: { subject: null, name: null }, approved: false, selectedNode: null,
      });
      toast(a.name + ' added to Connectors (not published) — opening editor', 'info');
    },
    finishNewVersion() {
      const ig = getState().ingest!;
      const id = ig.targetAdapterId!;
      const vstr = (ig.versionVendor || '').trim();
      const vs = versionsFor(id, getState().versions, getState().adapters);
      if (vs.some(v => v.vendor === vstr)) return toast('Version ' + vstr + ' already exists', 'warn');
      dispatch({ type: 'ADD_VERSION', id, vstr });
      toast('Draft mapping ingested · ' + nameOf(id, ints()) + ' v1 × ' + vstr, 'success');
    },
    addVersionFromDuplicate() {
      const ig = getState().ingest!;
      const id = ig.dupAdapterId!;
      const vstr = (ig.versionVendor || '').trim();
      if (!vstr) return toast('Enter the new vendor version', 'warn');
      const vs = versionsFor(id, getState().versions, getState().adapters);
      if (vs.some(v => v.vendor === vstr)) return toast('Version ' + vstr + ' already exists', 'warn');
      dispatch({ type: 'ADD_VERSION', id, vstr });
      toast('Draft mapping ingested · ' + nameOf(id, ints()) + ' v1 × ' + vstr, 'success');
    },
    openDupExisting() { const id = getState().ingest!.dupAdapterId!; patch({ ingest: null }); this.editInBuilder(id); },

    // FE↔BE integration: expose the internal toast helper so the boot loader
    // (and other async API flows) can surface failures through the same pipeline.
    toastRaw(msg: string, tone?: string) { toast(msg, tone); },

    // expose helpers needed by selectors/components
    _activeAdapter: activeAdapter,
    _ints: ints,
    _stepLabels: STEP_LABELS,
    _allInts: INTS,
  };
}

export function BridgeProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const timers = useRef<number[]>([]);

  const actions = useMemo(() => buildActions(() => stateRef.current, dispatch, timers), []);

  // FE↔BE integration (Round 1) — boot-time directory load. When USE_BACKEND is
  // on, replace the built-in INTS catalog and the connection/route state with
  // live backend data. We mutate INTS in place (applyBackendIntegrations) and
  // then PATCH conn/routes; the PATCH re-renders, and useDirectory's memo (keyed
  // on state) recomputes against the now-updated INTS. On failure we do NOT fall
  // back to mock seed data — a silent fallback renders identically to live state
  // (e.g. "Connected" cards) and masks real outages like a CORS/auth failure.
  // Instead we set directoryLoadError, which AppShell renders as a blocking
  // banner so the app fails loudly instead of lying about its data source.
  useEffect(() => {
    if (!USE_BACKEND) return;
    let cancelled = false;
    const bindings = seedBindings(); // capture FE bindings BEFORE INTS is mutated
    (async () => {
      try {
        const data = await loadDirectory(bindings);
        if (cancelled) return;
        applyBackendIntegrations(data.integrations);
        dispatch({
          type: 'PATCH',
          patch: {
            conn: data.conn,
            routes: data.routes,
            connBySlug: data.connBySlug,
            slugById: data.slugById,
            // Seed authCfg so the connect modal offers exactly the BE-allowed
            // methods (Round 2 fix for the auth_method mismatch).
            authCfg: data.authCfg,
            grantIdByRoute: data.grantIdByRoute, // Round 3: route → grant_id for revoke
            directoryLoadError: null,
          },
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load connectors';
        dispatch({ type: 'PATCH', patch: { directoryLoadError: msg } });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [actions]);

  // MCP-OAuth: listen for the BE callback's postMessage from the consent popup.
  // On success, finish the connect for whichever connector is mid-OAuth.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const d = e.data;
      if (!d || d.type !== 'sk-bridge-oauth') return;
      actions.finishOAuthConnect(d.ok === true);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [actions]);

  const value = useMemo<Ctx>(() => ({ state, actions }), [state, actions]);
  return <BridgeContext.Provider value={value}>{children}</BridgeContext.Provider>;
}

export function useBridge(): Ctx {
  const ctx = useContext(BridgeContext);
  if (!ctx) throw new Error('useBridge must be used within BridgeProvider');
  return ctx;
}
